<template>
  <div 
    class="diagram-wrapper" 
    :class="{ 'in-view': isInView }"
    ref="diagramWrapper"
  >
    <!-- Premium metallic header with steel design -->
    <div class="header-container">
      <div class="metallic-accent left"></div>
      <div class="metallic-accent right"></div>
      
      <h1 class="diagram-title">
        <!-- Add top accent line -->
        <div class="title-accent top"></div>
        <span class="title-text">{{ title }}</span>
        <!-- Rename to bottom accent for consistency -->
        <div class="title-accent bottom"></div>
      </h1>
    </div>
    <div class="flex flex-row">
      <div class="flex flex-col">
        <!-- Interactive Diagram Container with Steel Elements -->
        <div 
          class="diagram-container"
          ref="diagramContainer"
        >
          <!-- Steel frame with corner accents -->
          <div class="steel-frame">
            <div class="frame-corner top-left"></div>
            <div class="frame-corner top-right"></div>
            <div class="frame-corner bottom-left"></div>
            <div class="frame-corner bottom-right"></div>
          </div>
          
          <!-- Main diagram image -->
          <img 
            :src="currentBaseImage" 
            :alt="`${title} Base`" 
            class="diagram-image" 
            ref="baseImage"
            @error="handleImageError"
            @load="handleImageLoaded"
          />
          
          <!-- Loading and error states -->
          <div v-if="imageLoading" class="diagram-overlay loading">
            <div class="spinner-container">
              <div class="spinner"></div>
              <p>Loading diagram...</p>
            </div>
          </div>
          
          <div v-if="imageError" class="diagram-overlay error">
            <div class="error-container">
              <font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="error-icon" />
              <p>Failed to load diagram</p>
              <button @click="retryLoadImage" class="retry-button">
                <font-awesome-icon :icon="['fas', 'rotate']" class="rotate-icon" />
                <span>Retry</span>
              </button>
            </div>
          </div>
          
          <!-- Enhanced ping indicators -->
          <div
            v-for="(point, index) in visiblePingPoints"
            :key="`ping-${index}`"
            :style="{ top: point.top, left: point.left }"
            class="ping-indicator"
            :class="{ 'active': popoverActive[point.originalIndex] }"
            @mouseenter="handleMouseEnter(point.originalIndex)"
            @mouseleave="handleMouseLeave(point.originalIndex)"
            @click="togglePopover(point.originalIndex)"
          >
            <div class="ping-inner">
              <div class="ping-dot"></div>
              <div class="ping-pulse"></div>
              <div class="ping-tooltip">
                {{ pingPoints[point.originalIndex].title }}
                <div class="tooltip-arrow"></div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Mobile specs summary -->
        <div class="mobile-specs">
          <div class="mobile-specs-header">
            <h3>{{ currentTitleImage ? currentTitleImage.title : (tableData.title || title) }} Specifications</h3>
          </div>
          <div class="mobile-specs-content">
            <div class="mobile-specs-item">
              <div class="item-label">Capacity:</div>
              <div class="item-value" v-html="tableData.capacity"></div>
            </div>
            <div class="mobile-specs-item" v-for="(feature, index) in tableData.features.slice(0,2)" :key="`mobile-feature-${index}`">
              <div class="item-check">
                <svg class="checkmark" viewBox="0 0 24 24">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
                </svg>
              </div>
              <div class="item-value">{{ feature }}</div>
            </div>
          </div>
        </div>
        
        <!-- Enhanced info box with dynamic content -->
        <div class="info-box-container">
          <div class="info-box" :class="{ 'active': infoTitle || infoText !== defaultInfoText }">
            <!-- Metallic corner accents -->
            <div class="box-corner top-left"></div>
            <div class="box-corner top-right"></div>
            <div class="box-corner bottom-left"></div>
            <div class="box-corner bottom-right"></div>
            
            <div class="info-header">
              <span class="header-title">{{ infoTitle }}</span>
            </div>
            <div class="info-content" v-html="infoText"></div>
          </div>
        </div>
      </div>
    
      
    <!-- Replaced triangle with better scaling connector -->
    <transition name="fade">
        
        <div class="sidebar">
          <!-- Replace custom gears with FontAwesome icons -->
          <div class="sidebar-icon top-left">
            <font-awesome-icon :icon="['fas', 'gear']" class="gear-icon" />
          </div>
          
          <div class="sidebar-inner">
            <!-- Steel-inspired redesigned table -->
            <div class="specs-table">
              <div class="table-header">
                <h3>{{ currentTitleImage ? currentTitleImage.title : (tableData.title || title) }}</h3>
                <button v-if="tableTitleImages.length > 1 || tableImages.length > 1" @click="nextImage" class="next-image-button">
                  <font-awesome-icon :icon="['fas', 'arrow-right']" />
                </button>
              </div>
              
              <div class="table-image-container">
                <transition name="fade" mode="out-in">
                  <img 
                    :key="currentImageIndex"
                    :src="currentImage" 
                    :alt="currentTitleImage ? currentTitleImage.title : (tableData.title || title)" 
                    class="table-image" 
                  />
                </transition>
              </div>
              
              <div class="specs-grid">
                <div class="specs-section capacity-section">
                  <div class="specs-label">Capacity</div>
                  <div class="specs-value" v-html="tableData.capacity"></div>
                  <div class="specs-check">
                    <svg class="checkmark" viewBox="0 0 24 24">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
                    </svg>
                  </div>
                </div>
                
                <div 
                  v-for="(feature, index) in tableData.features" 
                  :key="`feature-${index}`" 
                  class="specs-section"
                >
                  <div class="specs-value">{{ feature }}</div>
                  <div class="specs-check">
                    <svg class="checkmark" viewBox="0 0 24 24">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
                    </svg>
                  </div>
                </div>
                
                <div class="specs-footer">
                  {{ tableData.description || "Interact with the diagram to learn about key features." }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, onUnmounted, watch } from 'vue';

export default {
  name: 'TrailerDiagram',
  props: {
    title: {
      type: String,
      required: true
    },
    baseImage: {
      type: String,
      required: true
    },
    fallbackImage: {
      type: String,
      default: ''
    },
    pingPoints: {
      type: Array,
      required: true
    },
    tableData: {
      type: Object,
      required: true
    },
    tableImages: {
      type: Array,
      default: () => []
    },
    tableTitleImages: {
      type: Array,
      default: () => []
    }
  },
  setup(props) {
    const currentImageIndex = ref(0);
    const popoverActive = reactive(Array(props.pingPoints.length).fill(false));
    const isInView = ref(false);
    const diagramWrapper = ref(null);
    const diagramContainer = ref(null);
    const baseImage = ref(null);
    const observer = ref(null);
    const defaultInfoText = 'Hover over or tap locations on the diagram to receive more detailed information on each part.';
    
    const infoTitle = ref('');
    const infoText = ref(defaultInfoText);
    
    // Image handling state
    const imageLoading = ref(true);
    const imageError = ref(false);
    const imageRetries = ref(0);
    const currentBaseImage = ref(props.baseImage);

    // OPTIMIZATION: Only render visible pings to reduce DOM elements
    const visiblePingPoints = computed(() => {
      return props.pingPoints.map((point, index) => ({
        ...point,
        originalIndex: index
      }));
    });

    // Add computed property for current title/image pair
    const currentTitleImage = computed(() => {
      if (props.tableTitleImages.length > 0) {
        return props.tableTitleImages[currentImageIndex.value];
      }
      return null;
    });

    // Update currentImage to use tableTitleImages if available
    const currentImage = computed(() => {
      if (props.tableTitleImages.length > 0) {
        return props.tableTitleImages[currentImageIndex.value].image;
      }
      if (props.tableImages.length === 0) {
        return '';
      }
      return props.tableImages[currentImageIndex.value];
    });

    // Update nextImage function to handle tableTitleImages
    function nextImage() {
      const maxIndex = props.tableTitleImages.length > 0
        ? props.tableTitleImages.length - 1
        : props.tableImages.length - 1;
      
      currentImageIndex.value = (currentImageIndex.value + 1) > maxIndex
        ? 0
        : currentImageIndex.value + 1;
    }
    
    function handleImageError() {
      console.error(`Failed to load image: ${currentBaseImage.value}`);
      imageLoading.value = false;
      imageError.value = true;
      
      // Try fallback image if available and we haven't already tried it
      if (props.fallbackImage && currentBaseImage.value !== props.fallbackImage && imageRetries.value < 2) {
        imageRetries.value++;
        currentBaseImage.value = props.fallbackImage;
      }
    }
    
    function handleImageLoaded() {
      imageLoading.value = false;
      imageError.value = false;
    }
    
    function retryLoadImage() {
      imageLoading.value = true;
      imageError.value = false;
      
      // Switch back to the original image if we were using fallback
      if (currentBaseImage.value !== props.baseImage) {
        currentBaseImage.value = props.baseImage;
      } else {
        // Force reload by temporarily setting to empty and then back
        currentBaseImage.value = '';
        setTimeout(() => {
          currentBaseImage.value = props.baseImage;
        }, 100);
      }
    }

    // Simplified ping interaction handlers
    function handleMouseEnter(index) {
      if (!popoverActive[index]) {
        infoTitle.value = props.pingPoints[index].title;
        infoText.value = props.pingPoints[index].text;
      }
    }

    function handleMouseLeave(index) {
      if (!popoverActive[index]) {
        // Don't reset if another point is active
        if (!popoverActive.some(active => active)) {
          infoTitle.value = '';
          infoText.value = defaultInfoText;
        }
      }
    }

    function togglePopover(index) {
      // Reset all others to false, toggle the selected one
      const wasActive = popoverActive[index];
      
      // Optimize updates to minimize repaints
      for (let i = 0; i < popoverActive.length; i++) {
        popoverActive[i] = (i === index && !wasActive);
      }
      
      if (!wasActive) {
        infoTitle.value = props.pingPoints[index].title;
        infoText.value = props.pingPoints[index].text;
      } else {
        infoTitle.value = '';
        infoText.value = defaultInfoText;
      }
    }

    function setupIntersectionObserver() {
      observer.value = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              isInView.value = true;
            }
          });
        },
        { threshold: 0.1 }
      );
      
      if (diagramWrapper.value) {
        observer.value.observe(diagramWrapper.value);
      }
    }

    onMounted(() => {
      setupIntersectionObserver();
      
      // Add initial delay to trigger entrance animation
      setTimeout(() => {
        isInView.value = true;
      }, 300);
    });

    onUnmounted(() => {
      if (observer.value) {
        observer.value.disconnect();
      }
    });

    // Watch for changes to ping points and update the active states
    watch(() => props.pingPoints, () => {
      // Reset active states when ping points change
      popoverActive.length = 0;
      props.pingPoints.forEach(() => popoverActive.push(false));
    }, { deep: true });

    return {
      currentImageIndex,
      currentImage,
      currentTitleImage,
      popoverActive,
      infoTitle,
      infoText,
      defaultInfoText,
      isInView,
      diagramWrapper,
      diagramContainer,
      baseImage,
      imageLoading,
      imageError,
      currentBaseImage,
      visiblePingPoints,
      
      nextImage,
      handleMouseEnter,
      handleMouseLeave,
      togglePopover,
      handleImageError,
      handleImageLoaded,
      retryLoadImage
    };
  }
};
</script>

<style scoped>
/* Base Container */
.diagram-wrapper {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 1rem;
  position: relative;
  opacity: 0;
  transform: translateY(40px);
  transition: opacity 0.8s ease-out, transform 0.8s cubic-bezier(0.16, 1, 0.3, 1);
  will-change: transform, opacity;
}

.diagram-wrapper.in-view {
  opacity: 1;
  transform: translateY(0);
}

/* Premium Metallic Header */
.header-container {
  position: relative;
  width: 100%;
  margin-bottom: 2.5rem;
  overflow: visible;
  z-index: 10; /* Lower z-index so sidebar can overlap */
}

.metallic-accent {
  position: absolute;
  height: 3px;
  background: linear-gradient(90deg, 
    rgba(183, 185, 186, 0.1),
    rgba(230, 232, 233, 0.7),
    rgba(183, 185, 186, 0.1)
  );
  z-index: 1;
}

.metallic-accent.left {
  top: 50%;
  left: -5%;
  width: 20%;
  transform: skewX(-30deg);
}

.metallic-accent.right {
  top: 60%;
  right: -5%;
  width: 15%;
  transform: skewX(30deg);
}

.diagram-title {
  text-align:left;
  position: relative;
  background: linear-gradient(90deg, 
    rgba(42, 43, 56, 0.95) 0%,
    rgba(29, 30, 38, 0.85) 100%
  );
  color: white;
  padding: 1.5rem 2rem;
  border-radius: 0.75rem;
  font-size: 2rem;
  font-weight: 700;
  margin: 0;
  z-index: 5;
  box-shadow: 
    0 10px 30px rgba(0, 0, 0, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.05);
  overflow: hidden;
}

.diagram-title::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.1) 0%,
    rgba(255, 255, 255, 0) 50%,
    rgba(255, 255, 255, 0.05) 100%
  );
  pointer-events: none;
}

.title-text {
  position: relative;
  z-index: 2;
  background: linear-gradient(135deg, white, #b7b9ba);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

/* Replace title-underline with title-accent for both top and bottom */
.title-accent {
  position: absolute;
  left: 2rem;
  height: 3px;
  background: linear-gradient(90deg, var(--color-primary), transparent);
}

.title-accent.top {
  top: 0.75rem;
  width: 80px;
}

.title-accent.bottom {
  bottom: 0.75rem;
  width: 120px;
}

/* Replaced Triangle with Better Connector */
.custom-overlay {
  position: relative;
  display: flex;
  justify-content: flex-end;
  z-index: 4;
}

.specs-connector {
  position: absolute;
  top: 0;
  right: 120px;
  width: 80px;
  height: 80px;
  z-index: 2;
}

.connector-line {
  position: absolute;
  top: 0;
  right: 10px;
  width: 2px;
  height: 100%;
  background: linear-gradient(to bottom, 
    var(--color-primary), 
    rgba(143, 0, 0, 0.2)
  );
}

.connector-dot {
  position: absolute;
  top: 0;
  right: 0;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: var(--color-primary);
  transform: translateX(10px) translateY(-10px);
  box-shadow: 0 0 15px rgba(143, 0, 0, 0.5);
  animation: pulse-dot 2s infinite;
}

@keyframes pulse-dot {
  0%, 100% {
    transform: translateX(10px) translateY(-10px) scale(1);
    opacity: 1;
  }
  50% {
    transform: translateX(10px) translateY(-10px) scale(1.2);
    opacity: 0.8;
  }
}

/* Steel-Inspired Specs Table */
.sidebar {
  width: 380px;
  transform-origin: top right;
  will-change: transform;
  margin-top: -80px;
  z-index: 30; /* Higher z-index to appear above header */
  position: relative;
}

/* Replace custom gear styling with FontAwesome icon styling */
.sidebar-icon {
  position: absolute;
  z-index: 15; /* Set to 15 to be above header (10) but below sidebar content (30) */
}

.sidebar-icon.top-left {
  top: -30px;
  left: -30px;
  animation: rotate-gear-ccw 30s linear infinite;
}

.sidebar-icon.top-right {
  top: -30px;
  right: -30px;
  animation: rotate-gear 30s linear infinite;
}

.gear-icon {
  color: rgba(143, 0, 0, 0.9);
  font-size: 5rem;
  filter: drop-shadow(0 0 5px rgba(143, 0, 0, 0.3));
}

@keyframes rotate-gear {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes rotate-gear-ccw {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(-360deg); }
}

.sidebar-inner {
  transform-style: preserve-3d;
  transition: transform 0.3s ease;
  position: relative; /* Add position relative to make z-index work */
  z-index: 25; /* Higher than gear icons (15) */
}

.specs-table {
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: 1rem;
  overflow: hidden;
  box-shadow: 
    0 15px 35px rgba(0, 0, 0, 0.3),
    0 5px 10px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.05);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  position: relative; /* Add position to create stacking context */
  z-index: 2; /* Create stacking context */
}

.specs-table:hover {
  transform: translateY(-5px);
  box-shadow: 
    0 20px 40px rgba(0, 0, 0, 0.4),
    0 10px 20px rgba(0, 0, 0, 0.2);
}

.table-header {
  background: linear-gradient(135deg, 
    #8f0000 0%, 
    #690000 100%
  );
  color: white;
  padding: 1.25rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative; /* Already positioned */
  overflow: hidden;
  z-index: 3; /* Higher z-index within its stacking context */
}

.table-header::after {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.2),
    transparent
  );
  animation: shine 3s infinite;
}

@keyframes shine {
  0% { left: -100%; }
  20% { left: 100%; }
  100% { left: 100%; }
}

.table-header h3 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.next-image-button {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.next-image-button:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: translateX(3px);
}

.table-image-container {
  background: linear-gradient(
    to bottom,
    rgba(240, 240, 240, 0.95),
    rgba(220, 220, 220, 0.95)
  );
  padding: 1rem;
  text-align: center;
}

.table-image {
  max-width: 100%;
  max-height: 150px;
  object-fit: contain;
  transition: transform 0.5s ease;
}

.table-image-container:hover .table-image {
  transform: scale(1.05);
}

.specs-grid {
  padding: 0.5rem;
  background: rgba(255, 255, 255, 0.05);
}

.specs-section {
  display: grid;
  grid-template-columns: 3fr 1fr;
  align-items: center;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  transition: background 0.3s ease;
}

.specs-section:hover {
  background: rgba(255, 255, 255, 0.02);
}

.capacity-section {
  background: rgba(255, 255, 255, 0.02);
}

.specs-label {
  color: var(--color-secondary);
  font-weight: 500;
  opacity: 0.8;
  font-size: 0.9rem;
}

.specs-value {
  color: var(--color-secondary);
  font-weight: 600;
  grid-column: 1;
}

.specs-check {
  grid-column: 2;
  justify-self: center;
}

.checkmark {
  width: 24px;
  height: 24px;
  fill: var(--color-primary);
  filter: drop-shadow(0 2px 3px rgba(0, 0, 0, 0.2));
  transition: transform 0.3s ease;
}

.specs-section:hover .checkmark {
  transform: scale(1.2);
}

.specs-footer {
  padding: 1rem;
  color: var(--color-secondary);
  opacity: 0.7;
  font-style: italic;
  text-align: center;
  font-size: 0.9rem;
  background: rgba(0, 0, 0, 0.1);
}

/* Modern Diagram Container */
.diagram-container {
  position: relative;
  width:90%;
  margin-left: 0;
  margin-top: 3rem;
  border-radius: 0.75rem;
  transition: transform 0.5s cubic-bezier(0.22, 1, 0.36, 1);
  will-change: transform;
}

.diagram-container:hover {
  transform: translateY(-5px);
}

/* Steel frame design elements */
.steel-frame {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 10;
}

.frame-corner {
  position: absolute;
  width: 40px;
  height: 40px;
  pointer-events: none;
}

.frame-corner.top-left {
  top: 0;
  left: 0;
  border-top: 2px solid rgba(183, 185, 186, 0.7);
  border-left: 2px solid rgba(183, 185, 186, 0.7);
  border-top-left-radius: 0.75rem;
}

.frame-corner.top-right {
  top: 0;
  right: 0;
  border-top: 2px solid rgba(183, 185, 186, 0.7);
  border-right: 2px solid rgba(183, 185, 186, 0.7);
  border-top-right-radius: 0.75rem;
}

.frame-corner.bottom-left {
  bottom: 0;
  left: 0;
  border-bottom: 2px solid rgba(183, 185, 186, 0.7);
  border-left: 2px solid rgba(183, 185, 186, 0.7);
  border-bottom-left-radius: 0.75rem;
}

.frame-corner.bottom-right {
  bottom: 0;
  right: 0;
  border-bottom: 2px solid rgba(183, 185, 186, 0.7);
  border-right: 2px solid rgba(183, 185, 186, 0.7);
  border-bottom-right-radius: 0.75rem;
}

.diagram-image {
  width: 100%;
  display: block;
  transition: transform 0.5s ease;
}

.diagram-container:hover .diagram-image {
  transform: scale(1.03);
}

.light-source {
  position: absolute;
  background: radial-gradient(
    circle,
    rgba(255, 255, 255, 0.1) 0%,
    transparent 70%
  );
}

.light-source.top {
  top: -20%;
  left: 30%;
  width: 40%;
  height: 40%;
}

.light-source.right {
  top: 30%;
  right: -20%;
  width: 40%;
  height: 40%;
}

.light-source.bottom {
  bottom: -20%;
  left: 40%;
  width: 40%;
  height: 40%;
}

/* Loading & Error States */
.diagram-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: rgba(22, 23, 30, 0.9);
  z-index: 20;
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
}

.spinner-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: white;
}

.error-icon {
  font-size: 2.5rem;
  color: var(--color-primary);
  margin-bottom: 1rem;
}

.retry-button {
  margin-top: 1rem;
  padding: 0.5rem 1.5rem;
  background: var(--color-primary);
  color: white;
  border: none;
  border-radius: 0.25rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: background 0.3s ease, transform 0.3s ease;
}

.retry-button:hover {
  background: #a00000;
  transform: translateY(-2px);
}

.rotate-icon {
  transition: transform 0.5s ease;
}

.retry-button:hover .rotate-icon {
  transform: rotate(360deg);
}

/* Enhanced Ping Indicators */
.ping-indicator {
  position: absolute;
  z-index: 20;
  cursor: pointer;
  height: 48px;
  width: 48px;
  transform: translate(-50%, -50%);
}

.ping-inner {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  transition: transform 0.2s ease;
}

.ping-indicator:hover .ping-inner {
  transform: scale(1.3);
}

.ping-dot {
  position: relative;
  width: 12px;
  height: 12px;
  background-color: white;
  border-radius: 50%;
  z-index: 2;
  box-shadow: 0 0 8px rgba(255, 255, 255, 0.8);
  transition: background-color 0.3s ease, box-shadow 0.3s ease;
}

.ping-indicator:hover .ping-dot {
  background-color: #ff8c00;
  box-shadow: 0 0 12px rgba(255, 140, 0, 0.8);
}

.ping-indicator.active .ping-dot {
  background-color: var(--color-primary);
  box-shadow: 0 0 12px rgba(143, 0, 0, 0.8);
}

.ping-pulse {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.3);
  z-index: 1;
  animation: ping-pulse 1.5s cubic-bezier(0, 0, 0.2, 1) infinite;
}

.ping-indicator:hover .ping-pulse {
  background-color: rgba(255, 140, 0, 0.3);
}

.ping-indicator.active .ping-pulse {
  background-color: rgba(143, 0, 0, 0.3);
}

@keyframes ping-pulse {
  0% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 1;
  }
  75%, 100% {
    transform: translate(-50%, -50%) scale(2);
    opacity: 0;
  }
}

.ping-tooltip {
  position: absolute;
  top: -35px;
  left: 50%;
  transform: translateX(-50%) scale(0);
  padding: 0.25rem 0.5rem;
  background-color: rgba(0, 0, 0, 0.8);
  color: white;
  border-radius: 4px;
  font-size: 0.75rem;
  white-space: nowrap;
  opacity: 0;
  transition: transform 0.3s ease, opacity 0.3s ease;
  z-index: 30;
  pointer-events: none;
}

.tooltip-arrow {
  position: absolute;
  bottom: -4px;
  left: 50%;
  transform: translateX(-50%) rotate(45deg);
  width: 8px;
  height: 8px;
  background-color: rgba(0, 0, 0, 0.8);
}

.ping-indicator:hover .ping-tooltip {
  transform: translateX(-50%) scale(1);
  opacity: 1;
}

/* Mobile Specs */
.mobile-specs {
  display: none;
  width: 100%;
  margin-top: 1.5rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: 0.75rem;
  box-shadow: 
    0 10px 30px rgba(0, 0, 0, 0.2),
    0 5px 10px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.mobile-specs-header {
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.mobile-specs-header h3 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--color-secondary);
}

.mobile-specs-content {
  display: grid;
  gap: 0.75rem;
}

.mobile-specs-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.item-label {
  font-weight: 500;
  color: var(--color-secondary);
  opacity: 0.8;
}

.item-value {
  font-weight: 600;
  color: var(--color-secondary);
}

.item-check {
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Info Box */
.info-box-container {
  width: 95%;
  height: 180px;
  margin: 2rem 0 0 0;
  position: relative;
}

.info-box {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  background: linear-gradient(
    135deg,
    rgba(240, 240, 240, 0.95),
    rgba(225, 225, 225, 0.95)
  );
  color: #333;
  border-radius: 0.75rem;
  overflow: hidden;
  box-shadow: 
    0 15px 35px rgba(0, 0, 0, 0.2),
    0 5px 15px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, opacity 0.3s ease, box-shadow 0.3s ease;
  min-height: 100px;
  transform: translateY(10px);
  opacity: 0.95;
}

.info-box.active {
  transform: translateY(0);
  opacity: 1;
  box-shadow: 
    0 20px 40px rgba(0, 0, 0, 0.3),
    0 10px 20px rgba(0, 0, 0, 0.1);
}

/* Box corner accents */
.box-corner {
  position: absolute;
  width: 25px;
  height: 25px;
  z-index: 1;
}

.box-corner.top-left {
  top: 0;
  left: 0;
  border-top: 2px solid var(--color-primary);
  border-left: 2px solid var(--color-primary);
  border-top-left-radius: 0.75rem;
}

.box-corner.top-right {
  top: 0;
  right: 0;
  border-top: 2px solid var(--color-primary);
  border-right: 2px solid var(--color-primary);
  border-top-right-radius: 0.75rem;
}

.box-corner.bottom-left {
  bottom: 0;
  left: 0;
  border-bottom: 2px solid var(--color-primary);
  border-left: 2px solid var(--color-primary);
  border-bottom-left-radius: 0.75rem;
}

.box-corner.bottom-right {
  bottom: 0;
  right: 0;
  border-bottom: 2px solid var(--color-primary);
  border-right: 2px solid var(--color-primary);
  border-bottom-right-radius: 0.75rem;
}

.info-header {
  background: linear-gradient(90deg, 
    var(--color-primary) 0%, 
    #a00000 100%
  );
  color: white;
  padding: 1rem 1.5rem;
  position: relative;
  overflow: hidden;
}

.header-title {
  position: relative;
  z-index: 2;
  font-weight: 600;
  font-size: 1.2rem;
}

.info-content {
  padding: 1.25rem 1.5rem;
  line-height: 1.5;
}

/* Fade transition */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

/* Responsive design */
@media (max-width: 1200px) {
  .sidebar {
    width: 320px;
  }
  
  .specs-connector {
    right: 90px;
  }
  
  .title-accent.top {
    width: 70px;
  }
  
  .title-accent.bottom {
    width: 100px;
  }

  .sidebar-icon .gear-icon {
    font-size: 1.75rem;
  }
}

@media (max-width: 992px) {
  .diagram-title {
    font-size: 1.75rem;
    padding: 1.25rem 1.5rem;
  }
  
  .diagram-container {
    width: 70%;
  }
  
  .info-box-container {
    width: 70%;
  }
  
  .title-accent.top {
    width: 60px;
  }
  
  .title-accent.bottom {
    width: 90px;
  }
}

@media (max-width: 768px) {
  .diagram-container {
    width: 100%;
    margin-bottom: 1.5rem;
  }
  
  .info-box-container {
    width: 100%;
    height: 210px;
  }
  
  .sidebar {
    display: none;
  }
  
  .mobile-specs {
    display: block;
  }
  
  .specs-connector {
    display: none;
  }
  
  .title-accent.top {
    width: 50px;
  }
  
  .title-accent.bottom {
    width: 80px;
  }

  .sidebar-icon {
    display: none;
  }
}

@media (max-width: 480px) {
  .diagram-title {
    font-size: 1.5rem;
    padding: 1rem 1.25rem;
  }
  
  .frame-corner {
    width: 25px;
    height: 25px;
  }
  
  .title-accent.top {
    width: 40px;
  }
  
  .title-accent.bottom {
    width: 70px;
  }
}
</style>
